import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Chip, CASE_CATEGORIES, CASE_STAGE_MAP } from '../utils/cases';
import { STAGE_MAP, STAGE_MAP_COURT } from '../utils/helpers';
import Modal from '../components/Modal';
import QuickInquiry, { INQUIRY_OUTCOMES, INQUIRY_OUTCOME_MAP, INQUIRY_TOPIC_MAP } from '../components/QuickInquiry';

const PERIODS = [{ d: 7, label: '7 дней' }, { d: 30, label: '30 дней' }, { d: 90, label: '90 дней' }, { d: 365, label: 'год' }];
const fmt = d => String(d).slice(8, 10) + '.' + String(d).slice(5, 7) + '.' + String(d).slice(2, 4);

// Журнал обращений целиком (17.10.2026): полный текст записи и кнопка «🌙 Разобрать ночью».
// Отмеченные ночью разбирает Claude (/root/tools/auto/dispatch.py inquiries) — утром задача с разбором и черновиком.
const NIGHT = {
  queued: { label: '🌙 В очереди на ночь', color: '#6366f1' },
  done:   { label: '✅ Разобрано',          color: '#10b981' },
  error:  { label: '⚠ Не получилось',      color: '#dc2626' },
};

// Текст обращения: дописанные «📝 Текст» куски (— дата —) длиннее LONG и длинное начало сворачиваются —
// вставленное решение суда или переписка не растягивают ленту, открываются по клику (20.10.2026)
const LONG = 400;
const firstLine = t => { const l = t.trim().split('\n').find(x => x.trim()) || ''; return l.length > 90 ? l.slice(0, 90) + '…' : l; };
function Fold({ title, text }) {
  return (
    <details style={{ margin: '6px 0' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--accent)', fontSize: 13 }}>
        {title} <span style={{ color: 'var(--text-muted)' }}>({text.length.toLocaleString('ru-RU')} знаков — показать)</span>
      </summary>
      <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, padding: '8px 10px', background: '#f8fafc', borderRadius: 6 }}>{text.trim()}</div>
    </details>
  );
}
function NoteText({ note }) {
  if (!note) return <div style={{ fontSize: 14 }}>—</div>;
  const parts = note.split(/\n*— (\d{2}\.\d{2}\.\d{4}) —\n/);
  const head = parts[0], extra = [];
  for (let i = 1; i < parts.length; i += 2) extra.push({ date: parts[i], text: parts[i + 1] || '' });
  return (
    <div style={{ fontSize: 14, lineHeight: 1.5 }}>
      {head.length > 1200
        ? <><div style={{ whiteSpace: 'pre-wrap' }}>{head.slice(0, 500).trim()}…</div><Fold title="Весь текст" text={head} /></>
        : <div style={{ whiteSpace: 'pre-wrap' }}>{head.trim()}</div>}
      {extra.map((x, i) => x.text.length > LONG
        ? <Fold key={i} title={`— ${x.date} — ${firstLine(x.text)}`} text={x.text} />
        : <div key={i} style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}><span style={{ color: 'var(--text-muted)' }}>— {x.date} —</span>{'\n'}{x.text.trim()}</div>)}
    </div>
  );
}

export default function InquiriesPage() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [outcome, setOutcome] = useState('');
  const [onlyNight, setOnlyNight] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);  // обращение в окне правки
  const [adding2, setAdding2] = useState(null);  // обращение, к которому дописываем текст
  const [extra, setExtra] = useState('');
  const [asking, setAsking] = useState(null);   // обращение, для которого пишем «что сделать ночью»
  const [request, setRequest] = useState('');
  const [toCase, setToCase] = useState(null);   // обращение → гражданское дело
  const [cf, setCf] = useState({});
  const [toClient, setToClient] = useState(null); // обращение → клиент банкротства (МФЦ / суд)
  const [bf, setBf] = useState({});
  const navigate = useNavigate();
  const load = () => api.get('/inquiries', { params: { days: 365 } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener('inquiry-saved', load);
    return () => window.removeEventListener('inquiry-saved', load);
  }, []);

  const since = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
  const list = rows.filter(r => String(r.inquiry_date).slice(0, 10) >= since)
    .filter(r => !outcome || r.outcome === outcome)
    .filter(r => !onlyNight || r.night_status);
  const queued = rows.filter(r => r.night_status === 'queued').length;

  const night = async (r, on) => {
    try { await api.patch(`/inquiries/${r.id}/night`, { on, request }); setAsking(null); setRequest(''); load(); }
    catch (e) { alert(e.response?.data?.error || 'Не получилось'); }
  };
  const openToCase = r => {
    setToCase(r);
    setCf({ client_name: r.name || '', category: CASE_CATEGORIES.some(c => c.key === r.topic) ? r.topic : 'documents', title: '', fee: '' });
  };
  const makeCase = async () => {
    try { const { data } = await api.post(`/inquiries/${toCase.id}/to-case`, cf); setToCase(null); navigate(`/cases/${data.id}`); }
    catch (e) { alert(e.response?.data?.error || 'Не получилось'); }
  };
  const makeClient = async () => {
    try { const { data } = await api.post(`/inquiries/${toClient.id}/to-client`, bf); setToClient(null); navigate(`/clients/${data.id}`); }
    catch (e) { alert(e.response?.data?.error || 'Не получилось'); }
  };
  const attach = async (r, list) => {
    if (!list?.length) return;
    const fd = new FormData();
    Array.from(list).forEach(x => fd.append('files', x));
    try { await api.post(`/inquiries/${r.id}/files`, fd); load(); }
    catch (e) { alert(e.response?.data?.error || 'Файлы не загрузились'); }
  };
  // «📝 Текст»: дописать вставленный текст (переписку, ответ клиента) в конец записи с датой
  const appendText = async () => {
    const t = extra.trim();
    if (!t) return;
    const stamp = new Date().toLocaleDateString('ru-RU');
    const note = adding2.note ? `${adding2.note}\n\n— ${stamp} —\n${t}` : t;
    try { await api.patch(`/inquiries/${adding2.id}`, { note }); setAdding2(null); setExtra(''); load(); }
    catch (e) { alert(e.response?.data?.error || 'Не сохранилось'); }
  };
  const del = async id => { if (window.confirm('Удалить запись?')) { await api.delete('/inquiries/' + id); load(); } };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Обращения</h1>
        <button className="btn btn-primary" onClick={() => setAdding(true)}>📞 Записать</button>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
        Отметьте <b>🌙 Разобрать ночью</b> те обращения, по которым нужна работа. Ночью Claude разберёт отмеченные:
        что просит человек, что нужно сделать, и подготовит черновик (шаблон, заявление, список документов).
        Утром по каждому будет задача в «Задачах» с разбором в комментарии 💬.
        {queued > 0 && <div style={{ marginTop: 6 }}>Сейчас в очереди на ночь: <b>{queued}</b></div>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {PERIODS.map(p => (
          <button key={p.d} className={'btn btn-sm ' + (days === p.d ? 'btn-primary' : 'btn-secondary')} onClick={() => setDays(p.d)}>{p.label}</button>
        ))}
        <select className="form-select" style={{ width: 'auto' }} value={outcome} onChange={e => setOutcome(e.target.value)}>
          <option value="">Все итоги</option>
          {INQUIRY_OUTCOMES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <button className={'btn btn-sm ' + (onlyNight ? 'btn-primary' : 'btn-secondary')} onClick={() => setOnlyNight(v => !v)}>🌙 Только ночной разбор</button>
      </div>

      {!list.length && <div className="empty-state">Нет записей</div>}
      {list.map(r => {
        const o = INQUIRY_OUTCOME_MAP[r.outcome];
        const t = INQUIRY_TOPIC_MAP[r.topic];
        const n = NIGHT[r.night_status];
        return (
          <div key={r.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              <b style={{ marginRight: 4 }}>№{r.id} · {fmt(r.inquiry_date)}</b>
              {o && <Chip color={o.color}>{o.label}</Chip>}
              {t && <Chip color={t.color}>{t.label}</Chip>}
              {n && <Chip color={n.color}>{n.label}</Chip>}
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{[r.channel, r.source].filter(Boolean).join(' · ')}</span>
              <span style={{ marginLeft: 'auto' }} />
              {!r.night_status && r.outcome !== 'spam' &&
                <button className="btn btn-sm btn-primary" onClick={() => { setAsking(r); setRequest(''); }}>🌙 Разобрать ночью</button>}
              {r.night_status === 'queued' && <button className="btn btn-sm btn-secondary" onClick={() => night(r, false)}>Убрать из очереди</button>}
              {r.night_status === 'error' && <button className="btn btn-sm btn-secondary" onClick={() => { setAsking(r); setRequest(r.night_request || ''); }}>🌙 Ещё раз</button>}
              {r.night_status === 'done' && r.night_task_id && <Link className="btn btn-sm btn-secondary" to="/tasks">Задача №{r.night_task_id}</Link>}
              {r.case_id && (() => { const st = CASE_STAGE_MAP[r.case_stage]; return (
                <Link className="btn btn-sm btn-secondary" to={`/cases/${r.case_id}`} style={st ? { borderColor: st.color, color: st.color } : null}>
                  Дело №{r.case_id}{st ? ` · ${r.case_stage === 'completed' ? '✅ ' : ''}${st.label}` : ''}</Link>); })()}
              {r.client_id && (() => { const ci = r.client_info || {}; const st = (ci.type === 'court' ? STAGE_MAP_COURT : STAGE_MAP)[ci.stage]; return (
                <Link className="btn btn-sm btn-secondary" to={`/clients/${r.client_id}`} style={st ? { borderColor: st.color, color: st.color } : null}>
                  {ci.type === 'court' ? 'Суд' : 'МФЦ'}: клиент №{r.client_id}{st ? ` · ${st.label}` : ''}</Link>); })()}
              {!r.case_id && !r.client_id && r.outcome !== 'spam' && <>
                <button className="btn btn-sm btn-secondary" title="Пришёл по гражданскому вопросу — завести дело" onClick={() => openToCase(r)}>➜ Дело</button>
                <button className="btn btn-sm btn-secondary" title="Пришёл по банкротству — завести клиента (МФЦ или суд)" onClick={() => { setToClient(r); setBf({ client_name: r.name || '', type: 'mfc' }); }}>➜ Банкротство</button>
              </>}
              <button className="btn btn-sm btn-secondary" title="Изменить текст, итог, имя, телефон, файлы" onClick={() => setEditing(r)}>✏ Изменить</button>
              <button className="btn btn-sm btn-secondary" title="Вставить текст (переписку, ответ клиента) — допишется в конец" onClick={() => { setAdding2(r); setExtra(''); }}>📝 Текст</button>
              <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }} title="Прикрепить документы, фото, скриншоты">
                📎 Файл
                <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,.odt,audio/*" style={{ display: 'none' }}
                  onChange={e => { attach(r, e.target.files); e.target.value = ''; }} />
              </label>
              <button className="btn-icon" title="Удалить" onClick={() => del(r.id)}>✕</button>
            </div>
            {(r.name || r.phone) && <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{[r.name, r.phone].filter(Boolean).join(' · ')}</div>}
            <NoteText note={r.note} />
            {r.files?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, fontSize: 13 }}>
                {r.files.map(x => <a key={x.id} href={`/api/inquiries/files/${x.id}`} target="_blank" rel="noreferrer"
                  style={{ padding: '3px 8px', borderRadius: 12, background: '#f1f5f9' }}>📎 {x.original_name}</a>)}
              </div>
            )}
            {r.night_request && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Что сделать ночью: {r.night_request}</div>}
          </div>
        );
      })}

      {asking && (
        <Modal title={`🌙 Ночной разбор — обращение №${asking.id}`} onClose={() => setAsking(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setAsking(null)}>Отмена</button>
            <button className="btn btn-primary" onClick={() => night(asking, true)}>Поставить на ночь</button>
          </>}>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 10, color: 'var(--text-muted)' }}>{asking.note}</div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>Что нужно сделать (можно не писать — Claude поймёт из записи):</div>
          <textarea className="form-textarea" rows={4} value={request} onChange={e => setRequest(e.target.value)}
            placeholder="например: сделать шаблон согласия родителей на занятия и риски травм" />
        </Modal>
      )}
      {toCase && (
        <Modal title={`➜ Дело из обращения №${toCase.id}`} onClose={() => setToCase(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setToCase(null)}>Отмена</button>
            <button className="btn btn-primary" onClick={makeCase}>Создать дело</button>
          </>}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Дело заводится на стадии «Договор», текст обращения попадёт в заметки дела, прикреплённые файлы — в файлы дела, у обращения будет итог «Взял в работу».
            Оплату внесите в самом деле — тогда она попадёт в аналитику.
          </div>
          <div className="form-group"><label className="form-label">Клиент</label>
            <input className="form-input" value={cf.client_name} onChange={e => setCf({ ...cf, client_name: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Категория</label>
            <select className="form-select" value={cf.category} onChange={e => setCf({ ...cf, category: e.target.value })}>
              {CASE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Суть дела (коротко)</label>
            <input className="form-input" value={cf.title} placeholder="например: шаблон согласия родителей для хореографа"
              onChange={e => setCf({ ...cf, title: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Стоимость, ₽</label>
            <input className="form-input" inputMode="numeric" value={cf.fee} onChange={e => setCf({ ...cf, fee: e.target.value.replace(/[^\d]/g, '') })} /></div>
        </Modal>
      )}
      {toClient && (
        <Modal title={`➜ Банкротство из обращения №${toClient.id}`} onClose={() => setToClient(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setToClient(null)}>Отмена</button>
            <button className="btn btn-primary" onClick={makeClient} disabled={!(bf.client_name || '').trim()}>Завести клиента</button>
          </>}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Клиент заводится на стадии «Консультация» в разделе МФЦ или Судебное — дальше как обычно (анкета, ФССП, договор).
            Текст обращения попадёт в заметки клиента, файлы — во вкладку «Файлы», у обращения будет итог «Взял в работу».
          </div>
          <div className="form-group"><label className="form-label">ФИО (фамилия имя отчество)</label>
            <input className="form-input" value={bf.client_name} onChange={e => setBf({ ...bf, client_name: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Процедура</label>
            <select className="form-select" value={bf.type} onChange={e => setBf({ ...bf, type: e.target.value })}>
              <option value="mfc">Через МФЦ</option><option value="court">Через суд</option>
            </select></div>
        </Modal>
      )}
      {adding && <QuickInquiry onClose={() => setAdding(false)} onSaved={load} />}
      {adding2 && (
        <Modal title={`📝 Текст к обращению №${adding2.id}`} onClose={() => setAdding2(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setAdding2(null)}>Отмена</button>
            <button className="btn btn-primary" onClick={appendText} disabled={!extra.trim()}>Добавить</button>
          </>}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            Вставьте текст (Ctrl+V или долгое нажатие → «Вставить»). Он допишется в конец записи с сегодняшней датой, прежний текст останется.
          </div>
          <textarea className="form-textarea" rows={10} autoFocus style={{ width: '100%', resize: 'vertical' }} value={extra}
            placeholder="Текст переписки, ответ клиента, расшифровка…" onChange={e => setExtra(e.target.value)} />
        </Modal>
      )}
      {editing && <QuickInquiry edit={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
